import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { dateFormat, getOtherProperties } from "../common/Utils";
import { FadeInUp } from "../Transitions";
import Portal from "../Portal";
import Inputtext from "../Inputtext/Inputtext";
import { CalendarProps, CalendarState } from "./PropsType";

const rows: number = 6;
const cols: number = 7;
const _locale = {
  dayNames: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  dayNamesMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  monthNames: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  monthNamesShort: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
};
const _localeCn = {
  dayNamesShort: ["周日", "周一", "周二", "周三", "周四", "周五", "周六"],
  monthNamesShort: [
    "1月",
    "2月",
    "3月",
    "4月",
    "5月",
    "6月",
    "7月",
    "8月",
    "9月",
    "10月",
    "11月",
    "12月",
  ],
};

export default function Calendar(props: CalendarProps) {
  const {
    className,
    firstDayOfWeek = 7,
    format = "yyyy-MM-dd",
    lang = "en",
    ...other
  } = props;
  const [data, setData] = useState<CalendarState>({
    value: "",
    visible: !!props.visible,
  });
  const todayDate: Date = new Date();
  const _selectLocale: any =
    (props.lang as string)?.toLowerCase() === "cn" ? _localeCn : _locale;
  const _week: string[] = _selectLocale.dayNamesShort;
  if (props.firstDayOfWeek === 1) {
    _week.unshift(_week[_week.length - 1]);
    _week.pop();
  }
  const now = new Date();
  const {
    currentMonth = now.getMonth(),
    currentYear = now.getFullYear(),
    value = "",
    calendar = [],
    currentDate,
  }: any = data;
  const [visible, setVisible] = useState(false);
  const styleClass = classNames("Calendar-panel", className);
  const otherProps: object = getOtherProperties(other, [
    "firstDayOfWeek",
    "format",
    "lang",
    "minDate",
    "maxDate",
    "onClose",
    "onChange",
    "onOpen",
    "value",
    "visible",
  ]);
  const getMonthFirstDay = (year: number, month: number) => {
    const curDate = new Date();
    if (year) {
      curDate.setFullYear(year);
    }
    if (month) {
      curDate.setMonth(month);
    }
    curDate.setDate(0);
    const dd = curDate.getDate();
    curDate.setDate(1);
    let item = curDate.getDay();
    if (item === 0) {
      item = 7;
    }
    return {
      total: dd,
      day: item,
    };
  };

  const getSomeDay = (day: string, add: number) => {
    const now = new Date(day);
    now.setDate(now.getDate() - add);
    const y = now.getFullYear();
    let m: any = now.getMonth() + 1;
    m = m < 10 ? "0" + m : m;
    let d: any = now.getDate();
    d = d < 10 ? "0" + d : d;
    return {
      y: y,
      m: m,
      d: d,
    };
  };

  const getDate = (currentDate: any) => {
    if (!(currentDate instanceof Date)) {
      currentDate = new Date(currentDate);
    }
    let currentYear, currentMonth, currentDay, currentWeek;
    currentYear = currentDate.getFullYear();
    currentMonth = currentDate.getMonth() + 1;
    currentDay = currentDate.getDate();
    currentWeek = currentDate.getDate();
    return {
      currentYear,
      currentMonth,
      currentDay,
      currentWeek,
    };
  };

  const setDate = (date: any = new Date()) => {
    if (date && typeof date === "string") {
      const tokens: string[] = date.trim().split(/\s+/);
      const dates = tokens[0].split(/[-\/]/gm);
      const arr = dates.map((d) => {
        return parseInt(d, 10);
      });
      date = new Date(arr[0], arr[1] - 1, arr[2]);
    }
    const dd: any = getDate(date);
    dd.currentDate = date;
    setData({
      ...data,
      ...dd,
    });

    return dd;
  };

  const splitNum = (v: any) => {
    return parseInt(v, 10);
  };

  const isDateValid = (value: any) => {
    if (/^\d{4}[-\/]\d{1,2}[-\/]\d{1,2}$/.test(value)) {
      const d = new Date(value);
      if (d && d.getDate()) {
        return true;
      }
    }
    return false;
  };

  const createCalendar = () => {
    const { minDate, maxDate } = props;
    let { currentYear, currentMonth }: any = data;
    const currentDay = data.currentDay;
    let calendar: any = [];
    if (currentMonth >= 13) {
      currentMonth = 1;
      currentYear += 1;
    } else if (currentMonth <= 0) {
      currentMonth = 12;
      currentYear -= 1;
    }
    const data2 = getMonthFirstDay(currentYear, currentMonth);
    let first = data2.day;
    const total = data2.total;
    const dd = getSomeDay(`${currentYear}-${currentMonth}-1`, 1);
    const selectedDay = splitNum(currentDay);

    if (firstDayOfWeek === 7) {
      first += 1;
      first = first === 8 ? 1 : first;
    }
    for (let i = 0; i < first - 1; i++) {
      const ymd = `${currentYear}-${currentMonth - 1}-${dd.d - first + i + 2}`;
      calendar.push({
        value: ymd,
        currentMonth: currentMonth - 1,
        date: dd.d - first + i + 2,
        type: "pass",
      });
    }
    for (let i = 1; i < total + 1; i++) {
      const ymd = `${currentYear}-${currentMonth}-${i}`;
      const isToday = i === todayDate.getDate();
      let type = "current";
      const cur = new Date(ymd);
      if (minDate && isDateValid(minDate)) {
        type = new Date(minDate) < cur ? "current" : "pass";
      }
      if (maxDate && isDateValid(maxDate)) {
        type = new Date(maxDate) > cur ? "current" : "pass";
      }
      calendar.push({
        value: ymd,
        currentYear: currentYear,
        currentMonth: currentMonth,
        date: i,
        type: type,
        selected: i === selectedDay,
        today:
          isToday &&
          currentMonth === todayDate.getMonth() + 1 &&
          currentYear === todayDate.getFullYear(),
      });
    }
    const both = total + first - 1;
    const b1 = (rows - 1) * cols;
    const b2 = rows * cols;
    const futureDate = both < b1 ? b1 - both : b2 - both;
    for (let i = 0; i < futureDate; i++) {
      const ymd = `${currentYear}-'${currentMonth + 1}-${i + 1}`;
      calendar.push({
        value: ymd,
        currentYear: currentYear,
        currentMonth: currentMonth + 1,
        date: i + 1,
        type: "future",
        selected: false,
      });
    }
    const months: any = [];
    let week: any = [];
    for (let i = 0; i < calendar.length; i++) {
      week.push(calendar[i]);
      if (i % 7 === 6) {
        months.push(week);
        week = [];
      }
    }
    calendar = months;
    return calendar;
  };

  const createWeek = () => {
    return _week.map((w, i) => {
      return (
        <th className="Calendar-item" key={"week_" + i}>
          {w}
        </th>
      );
    });
  };

  const createDate = () => {
    return createCalendar().map((w: any, i: number) => {
      return (
        <tr key={"row_" + i}>
          {w.map((day: any, j: number) => {
            const styleClass = classNames("Calendar-item", day.type, {
              selected: day.selected,
              today: day.today,
            });
            return (
              <td
                key={"col_" + j}
                className={styleClass}
                onClick={onDateSelect.bind(this, day, j)}
              >
                <span>{day.date}</span>
              </td>
            );
          })}
        </tr>
      );
    });
  };

  const selectDate = (value: any, event: any) => {
    const date = setDate(value);
    const selectedValue = dateFormat(date.currentDate, format);
    setData({
      ...data,
      value: selectedValue,
    });
    if (props.onChange && event) {
      (props.onChange as (event: any) => void)(selectedValue);
    }
  };

  const selectReset = () => {
    for (const day of calendar) {
      (day as Array<any>).forEach((v: any) => {
        v.selected = false;
      });
    }
  };

  const onDateSelect = (item: any, event: any) => {
    if (item.type === "current") {
      selectReset();
      item.selected = true;
      selectDate(item.value, event);
      handleClose();
    }
  };

  const toChangeDate = (add: number, year: boolean = false) => {
    if (year) {
      setData({
        ...data,
        currentYear: currentYear + add,
      });
    } else {
      setData({
        ...data,
        currentMonth: currentMonth + add,
      });
    }
  };

  const handleClose = () => {
    setVisible(false);
    if (props.onClose) {
      (props.onClose as () => void)();
    }
  };

  const handleClick = () => {
    setVisible(true);
  };

  useEffect(() => {
    setDate(todayDate);
    if (props.defaultValue) {
      selectDate(props.defaultValue, null);
    }
  }, []);

  useEffect(() => {
    if (props.visible) {
      if (props.onOpen) {
        (props.onOpen as () => void)();
      }
    } else {
      if (props.onClose) {
        (props.onClose as () => void)();
      }
    }
    setVisible(!!props.visible);
  }, [props.visible]);

  return (
    <div className="Calendar">
      <Inputtext readOnly={true} onClick={handleClick} value={value} />
      <Portal visible={visible!} mountNode={document.body}>
        <div className={styleClass} {...otherProps}>
          <div className="Calendar-mask" onClick={handleClose} />
          <FadeInUp in={visible!} className="Calendar-wrapper">
            <div className="Calendar-wrapper">
              <div className="Calendar-header">
                <div className="Calendar-select">
                  <div
                    className="Calendar-select-prev"
                    onClick={toChangeDate.bind(this, -1, false)}
                  >
                    <i className="icon icon-prev" />
                  </div>
                  <div className="Calendar-month">
                    {_selectLocale.monthNamesShort[currentMonth! - 1]}
                  </div>
                  <div
                    className="Calendar-select-next"
                    onClick={toChangeDate.bind(this, 1, false)}
                  >
                    <i className="icon icon-next" />
                  </div>
                </div>
                <div className="Calendar-select">
                  <div
                    className="Calendar-select-prev"
                    onClick={toChangeDate.bind(this, -1, true)}
                  >
                    <i className="icon icon-prev" />
                  </div>
                  <div className="Calendar-year">{currentYear}</div>
                  <div
                    className="Calendar-select-next"
                    onClick={toChangeDate.bind(this, 1, true)}
                  >
                    <i className="icon icon-next" />
                  </div>
                </div>
              </div>
              <div className="Calendar-body">
                <table>
                  <thead>
                    <tr>{createWeek()}</tr>
                  </thead>
                  <tbody className="Calendar-table-body">{createDate()}</tbody>
                </table>
              </div>
            </div>
          </FadeInUp>
        </div>
      </Portal>
    </div>
  );
}
